import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import payrollService from '../services/payrollService';
import {
  Plus, Eye, Trash, Download, Upload, Check, X,
  CaretDown, Info, Warning, CircleNotch, Gear
} from '@phosphor-icons/react';
import '../styles/DeductionRulesPage.css';

function DeductionRulesPage() {
  const [rules, setRules] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  
  // Modals
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'salary_bracket',
    operator: 'greater_than',
    condition_field: 'basic_salary',
    condition_value_min: '',
    condition_value_max: '',
    deduction_type: 'percentage',
    deduction_amount: '',
    deduction_category: 'other',
    deduction_description: '',
    priority: 0,
    notes: ''
  });
  
  // Test state
  const [testData, setTestData] = useState({
    staff_id: '',
    basic_salary: 5000,
    metadata: {}
  });
  const [testResults, setTestResults] = useState(null);
  
  // Dialog state
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });
  
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };
  
  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  
  // Load rules and presets
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await payrollService.listRules();
      setRules(response.items || []);
    } catch (error) {
      console.error('Error loading rules:', error);
      toast.error('Failed to load deduction rules');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadPresets = useCallback(async () => {
    try {
      const response = await payrollService.getRulePresets();
      setPresets(response.presets || []);
    } catch (error) {
      console.error('Error loading presets:', error);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadPresets();
  }, [loadRules, loadPresets]);
  
  const handleCreateRule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        deduction_amount: parseFloat(formData.deduction_amount),
        priority: parseInt(formData.priority)
      };
      
      if (formData.condition_value_min) {
        payload.condition_value_min = parseFloat(formData.condition_value_min);
      }
      if (formData.condition_value_max) {
        payload.condition_value_max = parseFloat(formData.condition_value_max);
      }
      
      await payrollService.createRule(payload);
      toast.success('Deduction rule created successfully!');
      setShowRuleModal(false);
      resetForm();
      loadRules();
    } catch (error) {
      toast.error(error.message || 'Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteRule = (ruleId) => {
    showDialog({
      title: 'Delete Deduction Rule',
      message: 'Are you sure you want to delete this deduction rule?',
      type: 'warning',
      primaryAction: {
        label: 'Delete',
        onClick: async () => {
          closeDialog();
          setSubmitting(true);
          try {
            await payrollService.deleteRule(ruleId);
            toast.success('Rule deleted successfully!');
            loadRules();
          } catch (error) {
            toast.error('Failed to delete rule');
          } finally {
            setSubmitting(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };
  
  const handleApplyPresets = async (selectedPresets) => {
    setSubmitting(true);
    try {
      await payrollService.applyRulePresets(selectedPresets);
      toast.success('Preset rules applied successfully!');
      setShowPresetsModal(false);
      loadRules();
    } catch (error) {
      toast.error(error.message || 'Failed to apply preset rules');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleTestRule = async (ruleId, modalTestData) => {
    const dataToTest = modalTestData || testData;
    if (!dataToTest.staff_id || !dataToTest.basic_salary) {
      toast.error('Please fill in all test fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await payrollService.testRule(
        ruleId,
        dataToTest.staff_id,
        parseFloat(dataToTest.basic_salary)
      );
      setTestResults(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test rule');
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rule_type: 'salary_bracket',
      operator: 'greater_than',
      condition_field: 'basic_salary',
      condition_value_min: '',
      condition_value_max: '',
      deduction_type: 'percentage',
      deduction_amount: '',
      deduction_category: 'other',
      deduction_description: '',
      priority: 0,
      notes: ''
    });
  };
  
  const getOperatorLabel = (operator) => {
    const labels = {
      'equals': 'Equals',
      'greater_than': 'Greater Than',
      'less_than': 'Less Than',
      'greater_equal': 'Greater or Equal',
      'less_equal': 'Less or Equal',
      'between': 'Between',
      'contains': 'Contains'
    };
    return labels[operator] || operator;
  };
  
  const getTypeColor = (type) => {
    const colors = {
      'salary_bracket': '#064E3B',
      'years_service': '#2196F3',
      'attendance': '#FF9800',
      'custom': '#9C27B0'
    };
    return colors[type] || '#666';
  };
  
  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Deduction Rules Engine</h1>
            <p>Define automatic deduction rules for payroll processing</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowPresetsModal(true)}
            >
              <Download size={18} weight="bold" /> Apply Presets
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowRuleModal(true)}
            >
              <Plus size={18} weight="bold" /> New Rule
            </button>
          </div>
        </div>
        
        {/* Rules List */}
        {loading ? (
          <div className="loading">Loading deduction rules...</div>
        ) : rules.length === 0 ? (
          <div className="empty-state">
            <p>No deduction rules configured</p>
            <div className="empty-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowRuleModal(true)}
              >
                Create First Rule
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPresetsModal(true)}
              >
                Or Apply Presets
              </button>
            </div>
          </div>
        ) : (
          <div className="rules-container">
            {rules.map((rule) => (
              <div key={rule.id} className="rule-card">
                <div className="rule-header">
                  <div className="rule-title">
                    <h3>{rule.name}</h3>
                    <span 
                      className="rule-type"
                      style={{ backgroundColor: getTypeColor(rule.rule_type) }}
                    >
                      {rule.rule_type}
                    </span>
                  </div>
                  <div className="rule-actions">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setSelectedRule(rule);
                        setShowDetailsModal(true);
                      }}
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setSelectedRule(rule);
                        setShowTestModal(true);
                      }}
                      title="Test rule"
                    >
                      <Gear size={16} />
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => handleDeleteRule(rule.id)}
                      title="Delete rule"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="rule-body">
                  {rule.description && (
                    <p className="rule-description">{rule.description}</p>
                  )}
                  
                  <div className="rule-details">
                    <div className="detail-item">
                      <label>Condition</label>
                      <p>
                        {rule.condition_field} {getOperatorLabel(rule.operator)}
                        {rule.condition_value_min && ` ${rule.condition_value_min}`}
                        {rule.condition_value_max && ` to ${rule.condition_value_max}`}
                      </p>
                    </div>
                    
                    <div className="detail-item">
                      <label>Deduction</label>
                      <p>
                        {rule.deduction_type === 'percentage'
                          ? `${rule.deduction_amount}% of salary`
                          : `GHS ${rule.deduction_amount.toFixed(2)}`}
                      </p>
                    </div>
                    
                    <div className="detail-item">
                      <label>Category</label>
                      <p>{rule.deduction_category}</p>
                    </div>
                    
                    <div className="detail-item">
                      <label>Priority</label>
                      <p>{rule.priority}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Create Rule Modal */}
        {showRuleModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>Create Deduction Rule</h2>
                <button className="close-btn" onClick={() => setShowRuleModal(false)}>
                  <X size={24} />
                </button>
              </div>
              
              <form onSubmit={handleCreateRule} className="form">
                <div className="form-group">
                  <label>Rule Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={submitting}
                    placeholder="e.g., Pension Bracket A"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={submitting}
                    placeholder="Describe what this rule does"
                    rows="2"
                  />
                </div>
                
                <div className="form-group">
                  <label>Rule Type *</label>
                  <select
                    value={formData.rule_type}
                    onChange={(e) => setFormData({ ...formData, rule_type: e.target.value })}
                    disabled={submitting}
                  >
                    <option value="salary_bracket">Salary Bracket</option>
                    <option value="years_service">Years of Service</option>
                    <option value="attendance">Attendance</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <fieldset className="form-section" disabled={submitting}>
                  <legend>Condition</legend>
                  
                  <div className="form-group">
                    <label>Condition Field *</label>
                    <input
                      type="text"
                      value={formData.condition_field}
                      onChange={(e) => setFormData({ ...formData, condition_field: e.target.value })}
                      placeholder="e.g., basic_salary, years_service"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Operator *</label>
                    <select
                      value={formData.operator}
                      onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                    >
                      <option value="equals">Equals</option>
                      <option value="greater_than">Greater Than</option>
                      <option value="less_than">Less Than</option>
                      <option value="greater_equal">Greater or Equal</option>
                      <option value="less_equal">Less or Equal</option>
                      <option value="between">Between</option>
                      <option value="contains">Contains</option>
                    </select>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Value (Min) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.condition_value_min}
                        onChange={(e) => setFormData({ ...formData, condition_value_min: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Value (Max)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.condition_value_max}
                        onChange={(e) => setFormData({ ...formData, condition_value_max: e.target.value })}
                        placeholder="For BETWEEN operator"
                      />
                    </div>
                  </div>
                </fieldset>
                
                <fieldset className="form-section" disabled={submitting}>
                  <legend>Deduction Details</legend>
                  
                  <div className="form-group">
                    <label>Deduction Type *</label>
                    <select
                      value={formData.deduction_type}
                      onChange={(e) => setFormData({ ...formData, deduction_type: e.target.value })}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (GHS)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Deduction Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.deduction_amount}
                      onChange={(e) => setFormData({ ...formData, deduction_amount: e.target.value })}
                      required
                      placeholder={formData.deduction_type === 'percentage' ? '0-100' : '0.00'}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Category *</label>
                    <input
                      type="text"
                      value={formData.deduction_category}
                      onChange={(e) => setFormData({ ...formData, deduction_category: e.target.value })}
                      placeholder="e.g., pension, tax, bonus"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={formData.deduction_description}
                      onChange={(e) => setFormData({ ...formData, deduction_description: e.target.value })}
                      placeholder="What is this deduction for?"
                    />
                  </div>
                </fieldset>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      disabled={submitting}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowRuleModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Rule Details Modal */}
        {showDetailsModal && selectedRule && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>{selectedRule.name}</h2>
                <button className="close-btn" onClick={() => setShowDetailsModal(false)}>
                  <X size={24} />
                </button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div className="detail-group">
                  <label>Description</label>
                  <p>{selectedRule.description || 'N/A'}</p>
                </div>
                
                <div className="detail-group">
                  <label>Rule Type</label>
                  <p style={{ textTransform: 'capitalize' }}>{selectedRule.rule_type}</p>
                </div>
                
                <div className="detail-group">
                  <label>Condition</label>
                  <p>
                    {selectedRule.condition_field} {getOperatorLabel(selectedRule.operator)}
                    {selectedRule.condition_value_min && ` ${selectedRule.condition_value_min}`}
                    {selectedRule.condition_value_max && ` to ${selectedRule.condition_value_max}`}
                  </p>
                </div>
                
                <div className="detail-group">
                  <label>Deduction</label>
                  <p>
                    {selectedRule.deduction_type === 'percentage'
                      ? `${selectedRule.deduction_amount}% of basic salary`
                      : `Fixed GHS ${selectedRule.deduction_amount.toFixed(2)}`}
                  </p>
                </div>
                
                <div className="detail-group">
                  <label>Category</label>
                  <p>{selectedRule.deduction_category}</p>
                </div>
              </div>
              
              <div className="form-actions">
                <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Presets Modal */}
        {showPresetsModal && (
          <PresetsModal
            presets={presets}
            onClose={() => setShowPresetsModal(false)}
            onApply={handleApplyPresets}
            loading={submitting}
          />
        )}
        
        {/* Test Modal */}
        {showTestModal && selectedRule && (
          <TestRuleModal
            rule={selectedRule}
            onClose={() => {
              setShowTestModal(false);
              setTestResults(null);
            }}
            onTest={handleTestRule}
            testResults={testResults}
            loading={submitting}
          />
        )}
        
        {/* Dialog Modal */}
        <DialogModal
          isOpen={dialogConfig.isOpen}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
        />
      </div>
    </Layout>
  );
}

// Presets Modal Component
function PresetsModal({ presets, onClose, onApply, loading }) {
  const [selectedPresets, setSelectedPresets] = useState([]);
  
  const togglePreset = (presetName) => {
    setSelectedPresets(prev =>
      prev.includes(presetName)
        ? prev.filter(p => p !== presetName)
        : [...prev, presetName]
    );
  };
  
  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Apply Preset Rules</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
          {presets.map((preset) => (
            <div key={preset.name} className="preset-item">
              <input
                type="checkbox"
                id={`preset-${preset.name}`}
                checked={selectedPresets.includes(preset.name)}
                onChange={() => togglePreset(preset.name)}
                disabled={loading}
              />
              <label htmlFor={`preset-${preset.name}`}>
                <strong>{preset.name}</strong>
                <p>{preset.description}</p>
              </label>
            </div>
          ))}
        </div>
        
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onApply(selectedPresets)}
            disabled={loading || selectedPresets.length === 0}
          >
            {loading ? 'Applying...' : `Apply ${selectedPresets.length} Presets`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Test Rule Modal Component
function TestRuleModal({ rule, onClose, onTest, testResults, loading }) {
  const [testData, setTestData] = useState({
    staff_id: '',
    basic_salary: 5000
  });
  
  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>Test Rule: {rule.name}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        {!testResults ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onTest(rule.id, testData);
            }}
            style={{ padding: '20px' }}
          >
            <div className="form-group">
              <label>Staff ID *</label>
              <input
                type="text"
                value={testData.staff_id}
                onChange={(e) => setTestData({ ...testData, staff_id: e.target.value })}
                required
                disabled={loading}
                placeholder="e.g., STF001"
              />
            </div>
            
            <div className="form-group">
              <label>Basic Salary (GHS) *</label>
              <input
                type="number"
                step="0.01"
                value={testData.basic_salary}
                onChange={(e) => setTestData({ ...testData, basic_salary: e.target.value })}
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Testing...' : 'Test Rule'}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ padding: '20px' }}>
            <div className={`test-result ${testResults.matched ? 'matched' : 'not-matched'}`}>
              {testResults.matched ? <Check size={32} /> : <X size={32} />}
              <p>{testResults.matched ? 'Rule Matched' : 'Rule Did Not Match'}</p>
            </div>
            
            {testResults.matched && (
              <div className="test-details">
                <p><strong>Deduction Amount:</strong> GHS {testResults.deduction_amount.toFixed(2)}</p>
                <p><strong>Type:</strong> {testResults.deduction_type}</p>
              </div>
            )}
            
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setTestResults(null);
                }}
              >
                Test Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeductionRulesPage;
